import {
  Box,
  Grid,
  Stack,
  Tooltip,
  Divider,
  Typography,
  IconButton
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { grey } from "@material-ui/core/colors";
import { Edit, Share, Favorite, OpenInNew } from "@material-ui/icons";
import { fNumber } from "utils/formatNumber";
import { createAdditionalPropertyDetailsForRealtor, timestampToDate } from "utils/listings/display";
import { NestedRowDetails } from "../../table/rows/NestedRowDetails";
import { CarouselThumbnail } from "components/carousel/CarouselThumbnail";
import { ListingPublisherCard } from "../../ListingPublisherCard";
import useFirestore from "hooks/useFirestore";
import { shareLink } from "utils/listings/helper";
import { convertListingToProcessedListingData } from "utils/listings/parser";
import {
  ListingAdditionalDetailsComponent,
  ListingBooleanPropertiesDetailsBag
} from "components/_dashboard/listingsMatch/ListingPropertiesAndAdditionalDetails";
import { ListingLocationDetails } from "components/_dashboard/listingsMatch/ListingLocationDetails";
import { ListingTableAdditionalNotes } from "../../table/rows/ListingTableAdditionalNotes";
import RoomsFloorAndSqrMtr from "components/_dashboard/listingsMatch/RoomsFloorAndSqrMtr";
import type { ListingWithAdditionalData } from "../../../../../@types/listings/new";
import { realtorSiteListingUrl } from "utils/realtorSite/realtorSiteUrls";

type ListingDetailsGeneralTabProps = {
  listing: ListingWithAdditionalData;
  isAuthorized: boolean;
  /** Toggles the listing in the user's favorites; the heart is hidden when absent. */
  handleClickFavorite?: () => Promise<void>;
  /** Opens the edit dialog; the edit button is hidden when absent. */
  handleEditListingDialogOpen?: (type?: "edit" | "duplicate") => () => void;
};

export function ListingDetailsGeneralTab({
  listing,
  isAuthorized,
  handleClickFavorite,
  handleEditListingDialogOpen
}: ListingDetailsGeneralTabProps): JSX.Element {
  const { listingData } = useFirestore();

  const { t: translate } = useTranslation();

  const {
    id,
    city,
    price,
    address,
    favorite,
    pictures,
    ownersData,
    createdTime,
    description,
    neighborhood,
    apartmentNumber
  } = listing;

  const processedListingData = convertListingToProcessedListingData(listing);

  const propertyDetailsForRealtor = createAdditionalPropertyDetailsForRealtor({
    listing: listing,
    isAuthorized: isAuthorized
  });

  const [owner] = ownersData ?? [];

  const { email, name = email, avatarUrl, phoneNumber, listingAlias } = owner ?? {};

  const customListingAlias = favorite && listingData ? listingData.listingAlias : listingAlias;

  const linkToListing = customListingAlias ? realtorSiteListingUrl(customListingAlias, id) : null;

  return (
    <Stack>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "normal", sm: "start" }}
        gap={{ xs: "initial", sm: 2 }}
      >
        <Box>
          <Typography sx={{ color: grey[800], wordBreak: "break-word" }} variant="h4">
            <Box
              sx={{
                display: "inline-flex",
                flexDirection: "column"
              }}
            >
              {address}
              {apartmentNumber && (
                <Typography sx={{ color: grey[700], mt: -0.5 }} variant="caption" fontSize="0.8rem">
                  {translate("listingsPage.apartment")} {apartmentNumber}
                </Typography>
              )}
            </Box>
            {neighborhood && (
              <>
                {", "}
                <Typography
                  sx={{ color: grey[700] }}
                  display="inline"
                  variant="h4"
                  component="span"
                >
                  {neighborhood}
                </Typography>
              </>
            )}
            {", "}
            <Typography sx={{ color: grey[600] }} display="inline" variant="h4" component="span">
              {city}
            </Typography>
          </Typography>
          <Typography sx={{ color: "primary.main" }} variant="h4">
            {`₪${fNumber(price)}`}
          </Typography>
        </Box>
        <Stack direction="column" alignItems="center">
          <Stack direction="row" justifyContent="center" spacing={1}>
            {linkToListing && (
              <>
                <Tooltip
                  title={translate(`listingsPage.control.share`)}
                  onClick={shareLink({
                    title: translate("listingsPage.listingLink"),
                    url: linkToListing
                  })}
                >
                  <IconButton>
                    <Share />
                  </IconButton>
                </Tooltip>
                <Tooltip title={translate("listingsPage.control.open")} arrow>
                  <IconButton href={linkToListing} target="_blank">
                    <OpenInNew />
                  </IconButton>
                </Tooltip>
              </>
            )}
            {handleClickFavorite && (
              <Tooltip title={translate("listingsPage.control.favorite")} arrow>
                <IconButton color={favorite ? "error" : "default"} onClick={handleClickFavorite}>
                  <Favorite />
                </IconButton>
              </Tooltip>
            )}
            {handleEditListingDialogOpen && (
              <Tooltip title={translate("listingsPage.control.edit")} arrow>
                <IconButton onClick={handleEditListingDialogOpen("edit")} disabled={!isAuthorized}>
                  <Edit />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
          <Typography sx={{ color: grey[600] }} variant="subtitle1">
            {timestampToDate(createdTime)}
          </Typography>
        </Stack>
      </Stack>

      <Divider sx={{ my: 2 }} />

      <CarouselThumbnail pictures={pictures} />

      <Divider sx={{ my: 2 }} />

      <RoomsFloorAndSqrMtr
        floor={listing.floor}
        roomsCount={listing.rooms}
        plotSizeM2={listing.sizeOfPlotM2}
        areaSizeM2={listing.sizeOfPropertyM2}
        displaySize="regular"
      />

      <Box sx={{ mt: 2 }} />

      <ListingLocationDetails listing={processedListingData} />

      <Box sx={{ mt: 2 }} />

      <ListingAdditionalDetailsComponent showAddressDisplayMode listing={processedListingData} />

      <Box sx={{ mt: 2 }} />

      <ListingBooleanPropertiesDetailsBag data={processedListingData} />

      <Box sx={{ mt: 3 }} />

      <Grid container spacing={2}>
        {propertyDetailsForRealtor.map((row) => (
          <Grid item xs={6} md={4} key={row.title}>
            <NestedRowDetails {...row} />
          </Grid>
        ))}
      </Grid>
      {description && (
        <>
          <Divider sx={{ my: 2 }} />
          <Stack spacing={1}>
            <Typography variant="h6">
              {translate("listingsPage.tableColumns.description")}
            </Typography>
            <Typography sx={{ overflowWrap: "break-word" }} variant="body2" whiteSpace="pre-line">
              {description}
            </Typography>
          </Stack>
        </>
      )}
      <ListingTableAdditionalNotes listing={listing} />
      <Divider sx={{ my: 2 }} />
      <ListingPublisherCard
        name={name}
        phoneNumber={phoneNumber}
        avatarUrl={avatarUrl}
        whatsAppMessage={
          linkToListing
            ? translate("listingsPage.publisher.whatsAppQuestion", "שאלה לגבי ") + linkToListing
            : undefined
        }
      />
    </Stack>
  );
}
